/* The connection settings form, shared by the onboarding wizard's step 2 and the
   company's Connection tab. Field ids take a prefix so both can be on one page. */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { Notice } from "../../../../design/components";
import type { ConnectionMethod } from "../../../../api/types";
import { erpDoctypesFor } from "../../../../domain/families";
import styles from "../onboard.module.css";

export const MASK = "••••";

export const SECRET_FIELDS = ["api_key", "api_secret"];

/* Every default the form displays is a value that has to be saved, so choosing a
   method seeds them into the draft rather than living only in the markup. */
export const METHOD_DEFAULTS: Record<ConnectionMethod, Record<string, string>> = {
  direct_api: { auth_mode: "api_key", poll_interval: "60" },
  agent: {},
  file: { file_format: "xml", schedule: "hourly", file_pattern: "{docType}-{name}.xml" },
  // The hub keeps no address or credential for a push company; its limits have server defaults.
  push: {},
};

export type MethodFormProps = {
  method: ConnectionMethod;
  /** Field id prefix; labels stay the same so both surfaces read alike. */
  prefix: string;
  code: string;
  value(key: string, fallback?: string): string;
  setConfig(patch: Record<string, string>): void;
  /** The tab renders the same fields read-only until Edit is pressed. */
  disabled: boolean;
  /** The company's document families; the doctypes polled are derived from them, not typed. */
  families: string[];
};

function Field({
  id,
  label,
  value,
  onChange,
  readOnly,
  disabled,
  wide,
}: {
  id: string;
  label: string;
  value: string;
  onChange?: (next: string) => void;
  readOnly?: boolean;
  disabled: boolean;
  wide?: boolean;
}) {
  return (
    <div className={`${styles.field}${wide ? ` ${styles.wide}` : ""}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className={`input${readOnly ? ` ${styles.readonly}` : ""}`}
        type="text"
        readOnly={readOnly}
        disabled={disabled}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      />
    </div>
  );
}

/** A stored secret comes back masked; `Replace` clears it so a new one can be typed.
    While one is being typed, `Show` reveals it so a pasted value can be checked. */
function Secret({
  id,
  label,
  value,
  masked,
  disabled,
  onChange,
  onReplace,
}: {
  id: string;
  label: string;
  value: string;
  masked: boolean;
  disabled: boolean;
  onChange(next: string): void;
  onReplace(): void;
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  return (
    <div className={styles.field}>
      <label htmlFor={id}>{label}</label>
      <div className={styles.secretRow}>
        <input
          id={id}
          className={`input${masked ? ` ${styles.readonly}` : ""}`}
          type={shown && !masked ? "text" : "password"}
          readOnly={masked}
          disabled={disabled}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {masked && !disabled ? (
          <button type="button" className="btn btn-sm" onClick={onReplace}>
            {t("onboard.connection.secret.replace", "Replace")}
          </button>
        ) : null}
        {!masked && !disabled ? (
          <button
            type="button"
            className="btn btn-sm"
            aria-label={t("onboard.connection.secret.showAriaLabel", "Show {{label}}", { label })}
            aria-pressed={shown}
            onClick={() => setShown((on) => !on)}
          >
            {shown ? t("onboard.connection.secret.hide", "Hide") : t("onboard.connection.secret.show", "Show")}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function MethodForm(props: MethodFormProps) {
  if (props.method === "direct_api") return <DirectApiForm {...props} />;
  if (props.method === "agent") return <AgentForm {...props} />;
  if (props.method === "push") return <PushForm />;
  return <FileForm {...props} />;
}

function DirectApiForm({ prefix, value, setConfig, disabled, families }: MethodFormProps) {
  const { t } = useTranslation();
  return (
    <>
      <p className={styles.caption}>{t("onboard.connection.direct.caption", "The hub calls this address outward.")}</p>
      <div className={styles.fields}>
        <Field
          id={`${prefix}-base-url`}
          label={t("onboard.connection.direct.baseUrl", "Base URL")}
          wide
          disabled={disabled}
          value={value("base_url")}
          onChange={(v) => setConfig({ base_url: v })}
        />
        <div className={styles.field}>
          <label htmlFor={`${prefix}-auth-mode`}>{t("onboard.connection.direct.authMode", "Authentication mode")}</label>
          <select id={`${prefix}-auth-mode`} className="select" disabled={disabled} value={value("auth_mode", "api_key")} onChange={(e) => setConfig({ auth_mode: e.target.value })}>
            <option value="api_key">{t("onboard.connection.direct.authModeApiKey", "API key and secret")}</option>
          </select>
        </div>
        <Field
          id={`${prefix}-integration-user`}
          label={t("onboard.connection.direct.integrationUser", "Integration user")}
          readOnly
          disabled={disabled}
          value={value("integration_user", t("onboard.connection.direct.integrationUserPlaceholder", "The API user the company created for the hub"))}
        />
        <Secret
          id={`${prefix}-api-key`}
          label={t("onboard.connection.direct.apiKey", "API key")}
          value={value("api_key")}
          masked={value("api_key") === MASK}
          disabled={disabled}
          onChange={(v) => setConfig({ api_key: v })}
          onReplace={() => setConfig({ api_key: "" })}
        />
        <Secret
          id={`${prefix}-api-secret`}
          label={t("onboard.connection.direct.apiSecret", "API secret")}
          value={value("api_secret")}
          masked={value("api_secret") === MASK}
          disabled={disabled}
          onChange={(v) => setConfig({ api_secret: v })}
          onReplace={() => setConfig({ api_secret: "" })}
        />
        <Field
          id={`${prefix}-polls`}
          label={t("onboard.connection.direct.doctypesPolled", "Doctypes polled")}
          wide
          readOnly
          disabled
          value={erpDoctypesFor(families).join(" · ")}
        />
        <Field
          id={`${prefix}-filters`}
          label={t("onboard.connection.direct.extraFilters", "Extra filters")}
          disabled={disabled}
          value={value("extra_filters")}
          onChange={(v) => setConfig({ extra_filters: v })}
        />
        <Field
          id={`${prefix}-poll`}
          label={t("onboard.connection.direct.pollInterval", "Poll interval (seconds)")}
          disabled={disabled}
          value={value("poll_interval", "60")}
          onChange={(v) => setConfig({ poll_interval: v })}
        />
        {/* Received invoices book their VAT to this account on the draft Purchase Invoice. */}
        <Field
          id={`${prefix}-vat-account`}
          label={t("onboard.connection.direct.vatAccount", "Input VAT account")}
          wide
          disabled={disabled}
          value={value("vat_account")}
          onChange={(v) => setConfig({ vat_account: v })}
        />
      </div>
    </>
  );
}

function AgentForm({ prefix, value, setConfig, disabled }: MethodFormProps) {
  const { t } = useTranslation();
  return (
    <>
      <p className={styles.caption}>
        {t(
          "onboard.connection.agent.caption",
          "The agent's connection is outbound only; no inbound port is opened on the company's network.",
        )}
      </p>
      <div className={styles.fields}>
        <Field
          id={`${prefix}-agent-host`}
          label={t("onboard.connection.agent.hostName", "Agent host name")}
          wide
          disabled={disabled}
          value={value("agent_host")}
          onChange={(v) => setConfig({ agent_host: v })}
        />
      </div>
      <Notice kind="info">
        {t(
          "onboard.connection.agent.enrolmentNotice",
          "The agent's enrolment token is issued on the company's Connection tab.",
        )}
      </Notice>
    </>
  );
}

function PushForm() {
  return (
    <>
      <p className={styles.caption}>
        The ERP sends invoices to the hub in batches. The hub does not connect to the ERP.
      </p>
      <Notice kind="info">
        The hub keeps no address or credential for this company. Its push token is issued in the
        next step, and the endpoint the ERP sends to is shown there.
      </Notice>
    </>
  );
}

function FileForm({ prefix, code, value, setConfig, disabled }: MethodFormProps) {
  const { t } = useTranslation();
  return (
    <>
      <p className={styles.caption}>{t("onboard.connection.file.caption", "No inbound firewall access is needed.")}</p>
      <div className={styles.fields}>
        <Field
          id={`${prefix}-drop-folder`}
          label={t("onboard.connection.file.dropFolder", "Drop folder")}
          wide
          readOnly
          disabled={disabled}
          value={`/drop/${code}/in`}
        />
        <div className={styles.field}>
          <label htmlFor={`${prefix}-file-format`}>{t("onboard.connection.file.fileFormat", "File format")}</label>
          <select id={`${prefix}-file-format`} className="select" disabled={disabled} value={value("file_format", "xml")} onChange={(e) => setConfig({ file_format: e.target.value })}>
            <option value="xml">{t("onboard.connection.file.formatXml", "XML")}</option>
            <option value="json">{t("onboard.connection.file.formatJson", "JSON")}</option>
            <option value="csv">{t("onboard.connection.file.formatCsv", "CSV")}</option>
          </select>
        </div>
        <div className={styles.field}>
          <label htmlFor={`${prefix}-schedule`}>{t("onboard.connection.file.schedule", "Schedule")}</label>
          <select id={`${prefix}-schedule`} className="select" disabled={disabled} value={value("schedule", "hourly")} onChange={(e) => setConfig({ schedule: e.target.value })}>
            <option value="15m">{t("onboard.connection.file.every15Min", "Every 15 minutes")}</option>
            <option value="hourly">{t("onboard.connection.file.hourly", "Hourly")}</option>
            <option value="daily">{t("onboard.connection.file.daily", "Daily")}</option>
          </select>
        </div>
        <Field
          id={`${prefix}-file-pattern`}
          label={t("onboard.connection.file.fileNamingPattern", "File-naming pattern")}
          disabled={disabled}
          value={value("file_pattern", "{docType}-{name}.xml")}
          onChange={(v) => setConfig({ file_pattern: v })}
        />
      </div>
    </>
  );
}
